import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { ipoService } from '../../api/ipoService';
import { mapIpoDetailResponse } from '../../utils/ipoDataMapper';
import { buildFilingView } from '../../utils/drhpFiling';

/**
 * One filing from the DRHP tracker: GET /api/core/ipos/drhp-tracker/{id}.
 *
 * Filings don't change once extracted, so every id loaded stays cached for the
 * session and revisiting a company costs no request.
 */
export const fetchDrhpFiling = createAsyncThunk(
  'drhpFiling/fetchDrhpFiling',
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await ipoService.getDrhpTrackerFiling(id);
      const record = response?.data;
      if (response?.success === false || !record) {
        return rejectWithValue(response?.error || 'Filing not found');
      }
      return { id: String(id), filing: buildFilingView(record, mapIpoDetailResponse(record)) };
    } catch (error) {
      // 404 means the id isn't in the tracker, which the page words differently
      return rejectWithValue(error.status === 404 ? 'No filing is tracked for this company.' : error.message || 'Failed to load the filing');
    }
  },
  {
    condition: ({ id, force = false }, { getState }) => {
      const { byId, loadingId } = getState().drhpFiling;
      if (loadingId === String(id)) return false;
      return force || !byId[String(id)];
    },
  }
);

const initialState = {
  byId: {},
  loadingId: null,
  status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
  error: null,
};

export const drhpFilingSlice = createSlice({
  name: 'drhpFiling',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchDrhpFiling.pending, (state, action) => {
        state.loadingId = String(action.meta.arg.id);
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchDrhpFiling.fulfilled, (state, action) => {
        state.loadingId = null;
        state.status = 'succeeded';
        state.byId[action.payload.id] = action.payload.filing;
      })
      .addCase(fetchDrhpFiling.rejected, (state, action) => {
        state.loadingId = null;
        state.status = 'failed';
        state.error = action.payload || 'Failed to load the filing';
      });
  },
});

export const selectDrhpFiling = (state, id) => state.drhpFiling.byId[String(id)] || null;

export default drhpFilingSlice.reducer;
